import * as am5 from '@amcharts/amcharts5';
import am5themesAnimated from '@amcharts/amcharts5/themes/Animated';
import * as am5map from '@amcharts/amcharts5/map';
import am5geodataWorldLow from '@amcharts/amcharts5-geodata/worldLow';
import am5geodataUsaLow from '@amcharts/amcharts5-geodata/usaLow';
import init from '../../../js/init';

const name = 'route-map';
const selector = '.route-map';
const mapSelector = `${selector}__map`;
const destinationsListSelector = `${selector}__destinations-list`;

const DEFAULT_ORIGIN = 'ontario, ca';
const cities = JSON.parse(document.querySelector('.airline-routes').getAttribute('data-cities'));

init(Drupal, name, selector, root => {
  const origin = (root.getAttribute('data-origin') || DEFAULT_ORIGIN).toLowerCase().trim();
  const colors = JSON.parse(root.getAttribute('data-colors') || '[]');

  const destinationsList = document.querySelector(destinationsListSelector);

  const mapElem = root.querySelector(mapSelector);

  // Create root element
  // https://www.amcharts.com/docs/v5/getting-started/#Root_element
  const mapRoot = am5.Root.new(mapElem);

  let firstDraw = true;
  let initialZoom = true;
  let dataInitialized = false;
  let currentState = 'world';
  let polygonSeries = null;
  let usaSeries = null;
  let dests = null;

  // Set themes
  // https://www.amcharts.com/docs/v5/concepts/themes/
  const customTheme = am5.Theme.new(mapRoot);
  customTheme.rule('Label').setAll({
    fontFamily: "'Montserrat', sans-serif",
  });

  mapRoot.setThemes([am5themesAnimated.new(mapRoot), customTheme]);

  // Create the map chart
  // https://www.amcharts.com/docs/v5/charts/map-chart/
  const chart = mapRoot.container.children.push(
    am5map.MapChart.new(mapRoot, {
      panX: "rotateX",
      panY: "translateY",
      rotationX: "-157.858093",
      projection: am5map.geoMercator(),
      minZoomLevel: 2,
      maxZoomLevel: 20,
      wheelZoom: false,
      wheelY: "none"
    })
  );

  chart.events.on("wheel", function(ev) {
    ev.preventDefault(); // Prevent default wheel behavior
    initialZoom = false;
    resetMap();
  });

  const graticuleSeries = chart.series.push(am5map.GraticuleSeries.new(mapRoot, {}));
  graticuleSeries.mapLines.template.setAll({
    stroke: mapRoot.interfaceColors.get('alternativeBackground'),
    strokeOpacity: 0.08,
  });
  graticuleSeries.zIndex = -10;

  // Create main polygon series for countries
  // https://www.amcharts.com/docs/v5/charts/map-chart/map-polygon-series/
  polygonSeries = chart.series.push(
    am5map.MapPolygonSeries.new(mapRoot, {
      geoJSON: am5geodataWorldLow,
      exclude: ["US"],
      templateField: "polygonSettings"
    })
  );
  polygonSeries.zIndex = 0;

  polygonSeries.setAll({
    geoJSON: am5geodataWorldLow
  });
  polygonSeries.mapPolygons.template.setAll({
    tooltipText: "{name}",
    interactive: true,
    fill: am5.color(0xc2cbd1),
    templateField: "polygonSettings"
  });

  polygonSeries.mapPolygons.template.states.create("hover", {
    fill: am5.color(0x21418b)
  });

  usaSeries = chart.series.push(
    am5map.MapPolygonSeries.new(mapRoot, {
      geoJSON: am5geodataUsaLow,
    })
  );
  usaSeries.zIndex = 0;

  usaSeries.setAll({
    geoJSON: am5geodataUsaLow
  });

  usaSeries.mapPolygons.template.setAll({
    tooltipText: "{name}",
    interactive: true,
    fill: am5.color(0xc2cbd1),
    templateField: "polygonSettings"
  });

  usaSeries.mapPolygons.template.states.create("hover", {
    fill: am5.color(0x21418b)
  });

  polygonSeries.mapPolygons.template.events.on("click", (ev) => {
    initialZoom = false;
    const dataItem = ev.target.dataItem;
    const data = dataItem.dataContext;
    const chosenCountry = data.id;
    let found = false;
    for (const cityItem in citySeries.dataItems) {
      if (citySeries.dataItems[cityItem].dataContext.country === chosenCountry) {
        found = true;
        break;
      }
    }
    if (found) {
      const filteredSeries = {...am5geodataWorldLow};
      filteredSeries.features = filteredSeries.features.filter(feature => feature.id === chosenCountry);
      if (filteredSeries.features.length > 0) {

        polygonSeries.dataItems.forEach(dataItem => {
          const id = dataItem.dataContext.id;
          const fill_color = colors['world']['states'].hasOwnProperty(id) && id === chosenCountry? colors['world']['states'][id] : 0xc2cbd1;
          dataItem._settings.mapPolygon.set('fill', am5.color(fill_color));
        });

        usaSeries.dataItems.forEach(dataItem => {
          dataItem._settings.mapPolygon.set('fill', am5.color(0xc2cbd1));
        });

        const zoomAnimation = polygonSeries.zoomToDataItem(dataItem);
        Promise.all([
          zoomAnimation.waitForStop()
        ]).then((results) => {
          currentState = 'state';
          citySeries.dataItems.forEach(dataItem => dataItem.dataContext.country !== chosenCountry ? citySeries.disposeDataItem(dataItem) : highlightDestination(dataItem.dataContext.id));
          lineSeries.dataItems.forEach(dataItem => dataItem.dataContext.country !== chosenCountry ? lineSeries.disposeDataItem(dataItem) : null);
          triggerChangeCountry(chosenCountry);
        });
      }
    }
  });

  usaSeries.mapPolygons.template.events.on("click", (ev) => {
    initialZoom = false;
    const dataItem = ev.target.dataItem;
    const data = dataItem.dataContext;
    const chosenState = data.id.toString().replace('US-', '');
    let found = false;
    for (const cityItem in citySeries.dataItems) {
      found = found || citySeries.dataItems[cityItem].dataContext.state === chosenState;
      if (found) break;
    }
    if (found) {
      const filteredSeries = {...am5geodataUsaLow};
      filteredSeries.features = filteredSeries.features.filter(feature => feature.id === data.id || feature.id === 'US-CA');
      if (filteredSeries.features.length > 1) {
        const zoomAnimation = usaSeries.zoomToDataItem(dataItem);

        polygonSeries.dataItems.forEach(dataItem => {
          dataItem._settings.mapPolygon.set('fill', am5.color(0xc2cbd1));
        });

        usaSeries.dataItems.forEach(dataItem => {
          const id = dataItem.dataContext.id;
          const clean_id = id.replace('US-', '');
          const fill_color = colors['world']['states'].hasOwnProperty(clean_id) ? colors['world']['states'][clean_id] : 0xc2cbd1;
          dataItem._settings.mapPolygon.set('fill', am5.color(fill_color));
        });

        Promise.all([
          zoomAnimation.waitForStop()
        ]).then((results) => {
          currentState = 'state';
          citySeries.dataItems.forEach(dataItem => dataItem.dataContext.state !== chosenState && dataItem.dataContext.id !== origin ? citySeries.disposeDataItem(dataItem) : (dataItem.dataContext.id !== origin ? highlightDestination(dataItem.dataContext.id) : null));
          lineSeries.dataItems.forEach(dataItem => dataItem.dataContext.state !== chosenState && dataItem.dataContext.id !== origin ? lineSeries.disposeDataItem(dataItem) : null);
          triggerChangeCountry(chosenState);
        });
      }
    }
  });


  const renderMap = function () {
    initialZoom = true;

    unhighlightDestinations();
    polygonSeries.show();
    usaSeries.show();

    if (!dataInitialized) {
      const worldData = [];
      for(const id in am5geodata_data_countries2) {
        if (am5geodata_data_countries2.hasOwnProperty(id)) {
          const country = am5geodata_data_countries2[id];
          if (country.maps.length) {
            worldData.push({
              id: id,
              map: country.maps[0],
              polygonSettings: {
                fill: am5.color(colors['world']['states'].hasOwnProperty(id) ? colors['world']['states'][id] : 0xc2cbd1),
                stroke: am5.color(colors['world']['border'] ? colors['world']['border'] : 0xffffff),
                strokeOpacity: 1,
                strokeWidth: 1
              }
            });
          }
        }
      }
      polygonSeries.data.setAll(worldData);

      const usaData = [];
      for(const id in am5geodataUsaLow.features) {
        if (am5geodataUsaLow.features.hasOwnProperty(id)) {
          const country = am5geodataUsaLow.features[id];
          if (country.id) {
            const clean_id = country.id.replace('US-', '');
            usaData.push({
              id: country.id,
              polygonSettings: {
                fill: am5.color(colors['usa']['states'].hasOwnProperty(clean_id) ? colors['usa']['states'][clean_id] : 0xc2cbd1),
                stroke: am5.color(colors['usa']['border'] ? colors['usa']['border'] : 0xffffff),
                strokeOpacity: 1,
                strokeWidth: 1
              }
            });
          }
        }
      }
      usaSeries.data.setAll(usaData);
      dataInitialized = true;
    }
    else {
      polygonSeries.dataItems.forEach(dataItem => {
        const id = dataItem.dataContext.id;
        const fill_color = colors['world']['states'].hasOwnProperty(id) ? colors['world']['states'][id] : 0xc2cbd1;
        dataItem._settings.mapPolygon.set('fill', am5.color(fill_color));
      });

      usaSeries.dataItems.forEach(dataItem => {
        const id = dataItem.dataContext.id;
        const clean_id = id.replace('US-', '');
        const fill_color = colors['usa']['states'].hasOwnProperty(clean_id) ? colors['usa']['states'][clean_id] : 0xc2cbd1;
        dataItem._settings.mapPolygon.set('fill', am5.color(fill_color));
      });
    }
  }

  // Create line series for trajectory lines
  // https://www.amcharts.com/docs/v5/charts/map-chart/map-line-series/
  const lineSeries = chart.series.push(am5map.MapLineSeries.new(mapRoot, {}));
  lineSeries.mapLines.template.setAll({
    stroke: mapRoot.interfaceColors.get('alternativeBackground'),
    strokeOpacity: 0.3,
  });
  lineSeries.zIndex = 20;

  // destination series
  const citySeries = chart.series.push(am5map.MapPointSeries.new(mapRoot, {}));

  citySeries.bullets.push(() => {
    const circle = am5.Circle.new(mapRoot, {
      radius: 6,
      tooltipText: '{title}',
      tooltipY: 0,
      fill: am5.color(0x21418b),
      stroke: mapRoot.interfaceColors.get('background'),
      strokeWidth: 1,
    });

    circle.states.create('hover', {
      scale: 1.5,
    });

    return am5.Bullet.new(mapRoot, {
      sprite: circle,
    });
  });
  citySeries.zIndex = 30;

  // arrow series
  const arrowSeries = chart.series.push(am5map.MapPointSeries.new(mapRoot, {}));

  arrowSeries.bullets.push(() => {
    const arrow = am5.Graphics.new(mapRoot, {
      fill: mapRoot.interfaceColors.get('alternativeBackground'),
      stroke: mapRoot.interfaceColors.get('alternativeBackground'),
      opacity: 0.3,
      draw: display => {
        display.moveTo(0, -2);
        display.lineTo(5, 0);
        display.lineTo(0, 2);
        display.lineTo(0, -2);
      },
    });

    return am5.Bullet.new(mapRoot, {
      sprite: arrow,
    });
  });

  const allCities = Object.keys(cities).map(key => ({
    id: key.toLowerCase(),
    focused: false,
    title: cities[key].city,
    state: cities[key].state,
    country: cities[key].country,
    geometry: {
      type: 'Point',
      coordinates: [cities[key].longitude, cities[key].latitude],
    },
  }));

  const originLongitude = cities[origin].longitude;
  const originLatitude = cities[origin].latitude;

  const changeHighlightDestination = (destination, value) => {
    const dataItem = citySeries.getDataItemById(destination.split(' (')[0]);
    const bulletSprite = dataItem.bullets !== undefined ? dataItem.bullets[0].get('sprite') : undefined;
    if (bulletSprite) {
      bulletSprite.set('fill', value ? am5.color(0xed2c85) : am5.color(0x21418b));
      if (value) {
        bulletSprite.hover();
      }
      else {
        bulletSprite.unhover();
      }
    }
  };

  const highlightDestination = destination => {
    changeHighlightDestination(destination, true);
  };

  const unhighlightDestination = destination => {
    changeHighlightDestination(destination, false);
  };

  const unhighlightDestinations = () => {
    citySeries.dataItems.forEach(dataItem => {
      const bulletSprite = dataItem.bullets !== undefined ? dataItem.bullets[0].get('sprite') : undefined;
      if (bulletSprite) {
        bulletSprite.unhover();
      }
    })
  };

  const drawMap = (destinations, dispose_map = false) => {
    if (!destinations.includes(origin)) destinations = [origin, ...destinations];

    unhighlightDestinations();
    polygonSeries.show();
    usaSeries.show();

    destinations = Array.from(new Set(destinations));
    const state_destinations = destinations.map((value) => value.split(',')[1].split(' (')[0].trim().toLowerCase());
    const codeless_destinations = destinations.map((value) => value.split(' (')[0]);
    const destCities = allCities.filter(({ id }) => codeless_destinations.includes(id));
    citySeries.data.setAll(destCities);
    const destCitiesObj = destCities.reduce((ac,a) => ({...ac,[a.id]:a}),{});
    lineSeries.dataItems.forEach(dataItem => lineSeries.disposeDataItem(dataItem));

    polygonSeries.dataItems.forEach(dataItem => {
      const id = dataItem.dataContext.id;
      const default_color = am5.color(colors['world']['states'].hasOwnProperty(id) ? colors['world']['states'][id] : 0xc2cbd1);
      const fill_color = dispose_map && !state_destinations.includes(id.toLowerCase()) ? 0xc2cbd1 : default_color;
      dataItem._settings.mapPolygon.set('fill', am5.color(fill_color));
    });

    usaSeries.dataItems.forEach(dataItem => {
      const id = dataItem.dataContext.id;
      const clean_id = id.replace('US-', '');
      const default_color = colors['usa']['states'].hasOwnProperty(clean_id) ? colors['usa']['states'][clean_id] : 0xc2cbd1;
      const fill_color = dispose_map && !state_destinations.includes(clean_id.toLowerCase()) ? 0xc2cbd1 : default_color;
      dataItem._settings.mapPolygon.set('fill', am5.color(fill_color));
    });


    const lineData = [];
    am5.array.each(destinations, did => {
      const did_city = did.split(' (')[0];
      const destinationDataItem = citySeries.getDataItemById(did_city);
      const destLongitude = destinationDataItem.get('longitude');
      const destLatitude = destinationDataItem.get('latitude');
      if (destLatitude === originLatitude && destLongitude === originLongitude) return;

      const geoFeature = {
        "city": destCitiesObj[did_city].title,
        "state": destCitiesObj[did_city].state,
        "country": destCitiesObj[did_city].country,
        "id": did_city,
        "focused": false,
        "geometry": {
          "type": "LineString",
          "coordinates": [
            [originLongitude, originLatitude],
            [destLongitude, destLatitude],
          ],
        }
      };
      lineData.push(geoFeature);
    });
    lineSeries.data.setAll(lineData);
  };

  const drawDestinations = destinations => {
    destinations.sort((a, b) =>
      a.destination < b.destination ? -1 : a.destination > b.destination ? 1 : 0
    );

    const destinationsGrouped = {};
    for (const { name, destination, airline } of destinations) {
      destinationsGrouped[name] = destinationsGrouped[name] || { destination, name: name, airlines: [] };
      destinationsGrouped[name].airlines.push(airline)
    }

    destinationsList.innerHTML = '';

    for (const name in destinationsGrouped) {
      const elem = document.createElement('div');
      const item = destinationsGrouped[name];
      const isSeasonal = item.name.includes(' *');

      elem.innerHTML = `
        <div class="flex flex-col">
          <span class="text-s font-bold text-primary-100">${item.name.split(" ").filter(function (word) {return word != '*'}).map(function (word) {return word.length > 2 ? word[0].toUpperCase() + word.substr(1) : word.toUpperCase()}).join(" ") + (isSeasonal ? ' *' : '') }</span>
          ${item.airlines.map(airline =>
            `<span class="text-xs">${airline}</span>`
          ).join('')}
        </div>
      `;
      destinationsList.appendChild(elem);
    }
  };

  const draw = (destinations, do_zoom, dispose_map = false) => {
    drawMap(destinations.map(d => d.name), dispose_map);
    if (firstDraw) {
      drawDestinations(destinations);
      firstDraw = false;
    }
    if (do_zoom) {
      const latitudes = destinations.map(d => cities[d.name.split(' (')[0]] ? parseFloat(cities[d.name.split(' (')[0]].latitude) : null)
      const center_latitude = latitudes.reduce((a, b) => a + b, 0) / latitudes.length
      const longitudes = destinations.map(d => cities[d.name.split(' (')[0]] ? parseFloat(cities[d.name.split(' (')[0]].longitude) : null)
      const center_longitude = longitudes.reduce((a, b) => a + b, 0) / longitudes.length
      chart.zoomToGeoPoint(
        { longitude: center_longitude, latitude: center_latitude },
        4,
        true
      );
    }
  };

  const resetMap = function () {
    const currentZoom = chart.get('zoomLevel');
    if (currentZoom < 8 && currentState !== 'world') {
      currentState = 'world';
      renderMap();
      doDraw(false);
      triggerMapReset();
    }
  };


  const doDraw = function (dispose_map) {
    draw(dests, false, dispose_map);
  }

  renderMap();

  const zoomControl = chart.set("zoomControl", am5map.ZoomControl.new(mapRoot, {}));
  zoomControl.events.on("click", function() {
    initialZoom = false;
    resetMap();
  })

  polygonSeries.events.on('datavalidated', () => {
    if (initialZoom) {
      chart.zoomToGeoPoint({ longitude: -157.858093, latitude: 40 }, 2, true);
    }
  });

  // Make stuff animate on load
  chart.appear(1000, 100);

  root.addEventListener('change-destinations', e => {
    const { destinations, do_zoom } = e.detail;
    let dispose_map = true;
    if (dests === null || destinations.length >= dests.length) {
      dests = destinations;
      dispose_map = false;
      chart.zoomToGeoPoint({ longitude: -157.858093, latitude: 40 }, 2, true);
    }
    initialZoom = false;
    draw(destinations, do_zoom, dispose_map);
  });

  root.addEventListener('reset-destinations', e => {
    const { destinations } = e.detail;
    dests = destinations;
    resetMap();
  });

  const triggerChangeCountry = (country) => {
    const event = new CustomEvent('change-country', {
      bubbles: true,
      detail: { country },
    });
    mapElem.dispatchEvent(event);
  };

  const triggerMapReset = () => {
    const event = new CustomEvent('map-reset', {
      bubbles: true
    });
    mapElem.dispatchEvent(event);
  };
});
